import {
  Alert,
  Button,
  Container,
  Flashbar,
  Form,
  FormField,
  Grid,
  Header,
  Input,
  Modal,
  SpaceBetween,
  Textarea,
} from '@cloudscape-design/components';
import { isEmpty, get, flatMap, toLower } from 'lodash';
import { useEffect, useState } from 'react';
import { useFormValidation } from 'src/hooks/useFormValidation';
import { TaskValidator } from 'src/validations/TaskValidator';
import RequiredFieldLabel from '../RequiredFieldLabel';
import {
  DEFAULT_LANGUAGE,
  EntityType,
  MARKDOWN_TEXT_WARNING,
  TASK_TYPE_LABELS,
  TaskType,
  ASSIGNEE_LABELS,
} from 'src/contstant/commonLabels';
import TextEditor, { FileData } from '../textEditor/TextEditor';
import { useTasks } from 'src/store/tasks/useTasks';
import useUserProfile from 'src/hooks/useUserProfile';
import ApiStatusFlashBar from '../ApiStatusFlashBar';
import { TaskDetails } from 'src/types/helios/Question';
import { CreateTaskRequest } from 'src/types/helios/Task';
import { Audit } from 'src/types/helios/Audit';
import {
  extractSrcValues,
  replaceImagesWithMediaIds,
  replaceMediaIdsWithPresignedUrlsAnyContent,
} from 'src/utils/mediaUtils';
import { useMediaUpload } from 'src/hooks/useMediaUpload';
import UploadProgressIndicator from '../UploadProgressIndicator';
import { useMedia } from 'src/store/media/useMedia';

interface TaskParams {
  taskName: string;
  taskDurationDays: number;
  taskDescription: string;
  assignee: string;
  taskType: string;
  auditId: string;
  questionId?: string;
  questionVersion?: string;
  subQuestionId?: string;
  auditorObservation: string;
  auditName?: string;
  questionTitle?: string;
}

interface CreateTaskFormProps {
  initialData?: TaskDetails;
  audit?: Audit;
  questionId?: string;
  questionVersion?: string;
  subQuestionId?: string;
  questionTitle?: string;
  onSave: (data: TaskParams) => void;
  onDismiss: () => void;
  visible: boolean;
  taskType: string;
}

const CreateTaskForm = ({
  initialData,
  visible,
  onSave,
  onDismiss,
  taskType,
  audit,
  questionId,
  questionVersion,
  subQuestionId,
  questionTitle,
}: CreateTaskFormProps) => {
  if (!audit) {
    return <></>;
  }
  const {
    createTask,
    createTaskFromQuestion,
    createTaskApiStatus,
    clearAPIStatusMessage,
    createTaskFromQuestionApiStatus,
  } = useTasks();
  const { getUserId, getUserProgramId, getLanguage } = useUserProfile();
  // Media upload hooks
  const { isUploading, uploadProgress, uploadImagesFromContent } = useMediaUpload();
  const { listMediaByIds, mediaList, listMediaApiStatus } = useMedia();
  const initValues: TaskParams = {
    taskName: initialData?.taskName || '',
    taskDurationDays: initialData?.taskDurationDays || 1,
    taskDescription: initialData?.taskDescription || '',
    assignee: '',
    taskType: taskType,
    auditId: audit.auditId,
    auditorObservation: '',
    questionId: questionId,
    questionVersion: questionVersion,
    subQuestionId: subQuestionId,
    auditName: audit.name,
    questionTitle: questionTitle,
  };
  const [fileList, setFileList] = useState<Record<string, FileData[]>>({});
  const [formData, setFormData] = useState<TaskParams>(initValues);
  const { fieldValidationError, validate, setFieldValidationErrors } = useFormValidation(
    formData,
    TaskValidator(),
  );

  const handleSave = async () => {
    if (!validate()) return;

    // Handle image uploads if any exist
    const images = flatMap(fileList) as FileData[];
    let description = formData.taskDescription;
    let taskMediaIdsMap = {};

    if (images.length > 0) {
      // Upload images and update description with media IDs
      const mediaIds = await uploadImagesFromContent(images, {
        programId: getUserProgramId(),
        businessUnit: audit.businessUnitId || audit.businessUnit,
        entityType: EntityType.TASK,
        createdBy: getUserId(),
      });

      if (mediaIds) {
        description = replaceImagesWithMediaIds(description, mediaIds);
      }

      taskMediaIdsMap = { taskDescription: extractSrcValues(description) };
    }

    const duration = String(formData.taskDurationDays);

    const auditorObservation =
      formData.auditorObservation === '' ? undefined : formData.auditorObservation;
    // Build task data
    const taskData: CreateTaskRequest = {
      businessUnitId: audit.businessUnitId || audit.businessUnit,
      taskName: formData.taskName,
      description,
      auditorObservation: auditorObservation,
      assigneeLogin: toLower(formData.assignee.trim()),
      auditId: formData.auditId,
      divisionName: audit.divisionName,
      locationId: audit.location,
      assignedBy: getUserId(),
      createdBy: getUserId(),
      taskType: formData.taskType,
      questionVersion: formData.questionVersion,
      templateId: audit.templateId,
      templateName: audit.templateName,
      auditName: formData.auditName,
      questionTitle: formData.questionTitle,
      duration,
      taskMediaIdsMap,
    };

    // Create task based on type
    if (taskType === TaskType.OBSERVATION) {
      createTask(taskData);
    } else {
      // Always pass task fields to the API — form fields are read-only when initialData exists
      const taskinfo = {
        taskName: taskData.taskName,
        description: taskData.description,
        duration: String(formData.taskDurationDays),
        taskMediaIdsMap: taskData.taskMediaIdsMap,
      };

      createTaskFromQuestion({
        businessUnitId: audit.businessUnitId || audit.businessUnit,
        questionId,
        questionVersion: formData.questionVersion,
        subQuestionId,
        auditId: audit.auditId,
        divisionName: audit.divisionName,
        locationId: audit.location,
        assigneeLogin: toLower(formData.assignee.trim()),
        auditorObservation: auditorObservation,
        taskType: formData.taskType,
        createdBy: getUserId(),
        language: DEFAULT_LANGUAGE,
        templateId: audit.templateId,
        templateName: audit.templateName,
        auditName: formData.auditName,
        questionTitle: formData.questionTitle,
        ...taskinfo,
      });
    }
  };

  // Clean up when form is closed from the view
  useEffect(() => {
    resetForm();
    if (visible) {
      getAllMedialIds();
    }
  }, [visible]);

  const getAllMedialIds = () => {
    if (initialData?.taskDescription) {
      const mediaIds = extractSrcValues(initialData?.taskDescription);
      if (mediaIds.length > 0) {
        listMediaByIds({
          programId: getUserProgramId(),
          mediaIds: mediaIds,
          businessUnit: audit.businessUnitId || audit.businessUnit,
        });
      }
    }
  };

  useEffect(() => {
    if (visible) {
      setFormData((pre) => ({
        ...pre,
        taskDescription:
          mediaList.length > 0
            ? replaceMediaIdsWithPresignedUrlsAnyContent(pre.taskDescription, mediaList)
            : pre.taskDescription,
      }));
    }
  }, [initialData?.taskDescription, mediaList]);

  // Handle successful task creation
  useEffect(() => {
    if (createTaskApiStatus.isSuccess || createTaskFromQuestionApiStatus.isSuccess) {
      onSave(formData);
      onDismiss();
    }
  }, [createTaskApiStatus.isSuccess, createTaskFromQuestionApiStatus.isSuccess]);

  const resetForm = () => {
    setFormData(initValues);
    setFieldValidationErrors({});
  };

  const renderActionButtons = () => (
    <SpaceBetween direction="horizontal" size="xs">
      <Button variant="link" onClick={onDismiss} data-testid="cancel-button">
        Cancel
      </Button>
      <Button
        data-testid="save-button"
        variant="primary"
        onClick={handleSave}
        loading={
          createTaskApiStatus.isLoading || createTaskFromQuestionApiStatus.isLoading
        }
      >
        Generate Task
      </Button>
    </SpaceBetween>
  );

  // Error state
  if (listMediaApiStatus.errorMessage) {
    return (
      <Alert type="error">
        Error loading meida details: {listMediaApiStatus.errorMessage}
      </Alert>
    );
  }

  return (
    <Modal
      data-testid="create-task-modal"
      visible={visible}
      onDismiss={onDismiss}
      header="Create Task"
      size="large"
    >
      <form data-testid="create-task-form" onSubmit={(e) => e.preventDefault()}>
        <ApiStatusFlashBar
          apiStatus={[
            createTaskApiStatus,
            createTaskFromQuestionApiStatus,
            listMediaApiStatus,
          ]}
          clearMessages={clearAPIStatusMessage}
        />
        <UploadProgressIndicator
          isUploading={isUploading}
          uploadProgress={uploadProgress}
        />
        <Form data-testid="task-form" actions={renderActionButtons()}>
          <SpaceBetween data-testid="form-content" size="xxl">
            {formData.taskType === TaskType.OBSERVATION && (
              <Alert data-testid="observation-alert" type="warning" header="Note:">
                Observation tasks can be created for ongoing audits. This task should
                address overall audit findings rather than individual failed question
              </Alert>
            )}
            <Grid
              data-testid="main-form-grid"
              gridDefinition={[
                { colspan: { default: 6 } },
                { colspan: { default: 3 } },
                { colspan: { default: 3 } },
              ]}
            >
              <FormField
                data-testid="task-name-field"
                label={<RequiredFieldLabel label="Task Name" />}
                stretch
                errorText={fieldValidationError.taskName}
              >
                <Input
                  data-testid="task-name-input"
                  readOnly={initialData?.taskName !== undefined}
                  value={formData.taskName}
                  onChange={({ detail }) => {
                    setFormData((prev) => ({
                      ...prev,
                      taskName: detail.value,
                    }));
                  }}
                />
              </FormField>
              <FormField
                data-testid="task-duration-field"
                label={<RequiredFieldLabel label="Task Duration (Days)" />}
                stretch
                errorText={fieldValidationError.taskDurationDays}
              >
                <Input
                  data-testid="task-duration-input"
                  type="number"
                  readOnly={initialData?.taskDurationDays !== undefined}
                  value={formData.taskDurationDays?.toString() || '1'}
                  onChange={({ detail }) => {
                    setFormData((prev) => ({
                      ...prev,
                      taskDurationDays: parseInt(detail.value) || 1,
                    }));
                  }}
                />
              </FormField>
              <FormField
                data-testid="assignee-field"
                label={<RequiredFieldLabel label={ASSIGNEE_LABELS.LABEL} />}
                stretch
                errorText={fieldValidationError.assignee}
              >
                <Input
                  data-testid="assignee-input"
                  value={formData.assignee}
                  onChange={({ detail }) => {
                    setFormData((prev) => ({
                      ...prev,
                      assignee: detail.value,
                    }));
                  }}
                  placeholder={ASSIGNEE_LABELS.PLACEHOLDER}
                />
              </FormField>
            </Grid>
            {taskType && (
              <Grid
                data-testid="task-details-grid"
                gridDefinition={[
                  { colspan: { default: 3 } },
                  { colspan: { default: 3 } },
                  { colspan: { default: 3 } },
                  { colspan: { default: 3 } },
                ]}
              >
                <FormField
                  data-testid="task-type-field"
                  label="Task Type"
                  stretch
                  errorText={fieldValidationError.taskType}
                >
                  <Input
                    data-testid="task-type-input"
                    value={TASK_TYPE_LABELS[formData.taskType as keyof typeof TaskType]}
                    readOnly={true}
                  />
                </FormField>

                <FormField
                  data-testid="audit-id-field"
                  label="Audit ID"
                  stretch
                  errorText={fieldValidationError.auditId}
                >
                  <Input
                    data-testid="audit-id-input"
                    value={formData.auditId || ''}
                    readOnly={true}
                    onChange={({ detail }) => {
                      setFormData((prev) => ({
                        ...prev,
                        auditId: detail.value,
                      }));
                    }}
                  />
                </FormField>

                {formData.questionId && (
                  <FormField
                    data-testid="question-id-field"
                    label="Question ID"
                    stretch
                    errorText={fieldValidationError.questionId}
                  >
                    <Input
                      data-testid="question-id-input"
                      value={formData.questionId}
                      readOnly={true}
                      onChange={({ detail }) => {
                        setFormData((prev) => ({
                          ...prev,
                          questionId: detail.value,
                        }));
                      }}
                    />
                  </FormField>
                )}
                {formData.subQuestionId && (
                  <FormField
                    data-testid="subquestion-id-field"
                    label="SubQuestion ID"
                    stretch
                    errorText={fieldValidationError.auditId}
                  >
                    <Input
                      data-testid="subquestion-id-input"
                      value={formData.subQuestionId}
                      readOnly={true}
                      onChange={({ detail }) => {
                        setFormData((prev) => ({
                          ...prev,
                          subQuestionId: detail.value,
                        }));
                      }}
                    />
                  </FormField>
                )}
                {formData.questionVersion && (
                  <FormField
                    data-testid="question-version-field"
                    label="Question Version"
                    stretch
                  >
                    <Input
                      data-testid="question-version-input"
                      value={formData.questionVersion}
                      readOnly={true}
                    />
                  </FormField>
                )}
              </Grid>
            )}

            <FormField
              data-testid="task-description-field"
              label={<RequiredFieldLabel label="Task Description" />}
              stretch
              description={MARKDOWN_TEXT_WARNING}
              errorText={fieldValidationError.taskDescription}
            >
              <TextEditor
                data-testid="task-description-editor"
                placeholder="Enter task description..."
                value={formData.taskDescription}
                readOnly={initialData?.taskDescription !== undefined}
                onChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    taskDescription: value,
                  }))
                }
                onChangeMedia={(data) =>
                  setFileList((prev) => ({ ...prev, taskDescription: data }))
                }
              />
            </FormField>

            <FormField
              stretch
              data-testid="auditorObservation-field"
              label="Auditor Observation"
              errorText={fieldValidationError.description}
            >
              <Textarea
                data-testid="auditorObservation-input"
                value={formData.auditorObservation}
                onChange={({ detail }) =>
                  setFormData((prevState) => ({
                    ...prevState,
                    auditorObservation: detail.value,
                  }))
                }
              />
            </FormField>
          </SpaceBetween>
        </Form>
      </form>
    </Modal>
  );
};

export default CreateTaskForm;
